import { useEffect, useState } from "react";
import { Button, notification } from "components/common";
import {
  useFollowUserMutation,
  useUnfollowUserMutation,
} from "features/community/apis";
import theme from "utils/themeVars";

/**
 * Follow button with approval-based states:
 *   none/rejected -> "Follow" (primary)
 *   pending       -> "Requested" (click cancels the request)
 *   accepted      -> "Following" (hover -> "Unfollow")
 *   self          -> hidden
 */
const FollowButton = ({
  userCode,
  relationship = "none",
  onChanged = () => {},
  size = "middle",
  block = false,
  variant = "default",
  followText = "Follow",
}) => {
  const [status, setStatus] = useState(relationship || "none");
  const [hover, setHover] = useState(false);

  useEffect(() => {
    setStatus(relationship || "none");
  }, [relationship]);

  const [followUser, { isLoading: following }] = useFollowUserMutation();
  const [unfollowUser, { isLoading: unfollowing }] = useUnfollowUserMutation();

  if (status === "self" || !userCode) return null;

  const busy = following || unfollowing;
  const isSubtle = variant === "subtle";
  const isOutline = variant === "outline";
  const buttonType = isSubtle ? "link" : isOutline ? "default" : "primary";

  const doFollow = async () => {
    const prev = status;
    setStatus("pending");
    const res = await followUser(userCode);
    if (res?.error) {
      setStatus(prev);
      notification.error({
        message: res?.error?.message || "Could not send follow request",
      });
      return;
    }
    const next = res?.data?.status || "pending";
    setStatus(next);
    onChanged(next);
  };

  const doUnfollow = async () => {
    const prev = status;
    setStatus("none");
    const res = await unfollowUser(userCode);
    if (res?.error) {
      setStatus(prev);
      notification.error({
        message: res?.error?.message || "Could not update follow",
      });
      return;
    }
    onChanged("none");
  };

  if (status === "accepted") {
    return (
      <Button
        size={size}
        block={block}
        loading={busy}
        type={buttonType}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={doUnfollow}
        style={
          hover
            ? {
                color: theme["error-color"],
                borderColor: theme["error-color"],
                ...(isOutline ? { background: "#fff" } : {}),
              }
            : undefined
        }
        text={hover ? "Unfollow" : "Following"}
      />
    );
  }

  if (status === "pending") {
    return (
      <Button
        size={size}
        block={block}
        loading={busy}
        type={buttonType}
        onClick={doUnfollow}
        text="Requested"
      />
    );
  }

  return (
    <Button
      size={size}
      block={block}
      loading={busy}
      type={buttonType}
      icon={isSubtle ? "FiUserPlus" : undefined}
      onClick={doFollow}
      text={followText}
    />
  );
};

export default FollowButton;
